/**
 * THE COMPANY — a place on the CHATS shelf that several Chats hang off (2026-08-26, founder-ruled).
 *
 * A Chat pack is one framed conversation with one person (`scenarios.ts`, the CONVERSATIONS list).
 * A COMPANY is the thing some of those people work for, and it is a page rather than a row because
 * a marketplace with two things on it is a listing, not a conversation. The shelf shows ONE row
 * for the company; the row opens this page; the page's controls open the packs.
 *
 * ⚠ IT IS A DESTINATION, NOT A GATE — the social-object law (chat-pack §4, library-first: *"a
 * scenario ID is a social object"*). `/p/the-argument` still lands a reader straight in the booth,
 * exactly as it did before this page existed, because a streamer's link must land where the
 * streamer was and never in a lobby. Nothing here is on that path: this file is a second, longer
 * way in, and `PackSurface` has never heard of it.
 *
 * ── THE CRAFT RULE, AND IT IS THE WHOLE OF THE PAGE (founder-ruled 2026-08-26) ───────────────
 * **The offers are presented completely seriously.** Tiers, rates, ratings, response times, terms,
 * a visible review — the way a real service listing presents them. NOTHING ON THIS PAGE WINKS.
 * The absurdity is entirely in *what* is being sold with a straight face; copy that is funny about
 * itself collapses the fiction into a sketch performed at the reader, and the reader stops being
 * the funny one (the allocation law: character = committed, player = free).
 *
 * Two consequences worth stating because they will look like omissions:
 *   · the one-star review is quoted in full and the seller's 61 replies are reported as a plain
 *     count. That is the funniest thing on the page and it is written as a fact about a listing;
 *   · the word "AI" appears nowhere, here or in any pack this page links to. The site sells
 *     bookings and includes a support assistant. What the assistant is made of is not the subject
 *     (wedge spec §3.1: the product is never about the medium).
 *
 * ⚠ EVERY CONTENT STRING BELOW IS A BUILDER PLACEHOLDER PENDING THE FOUNDER'S PEN. The name is
 * declared ONCE PER LOCALE, on `entity` and `locales`, and nothing else in this file or on the
 * page repeats it — the copy says "the site" wherever it would otherwise say the name — so a
 * rename is one edit per language and nothing else moves.
 *
 * ── THE NAME IS NATIONAL (founder-ruled 2026-08-26) ───────────────────────────────────────────
 * The placeholder *Handled* is retired. The company is named with a dignified, slightly pompous
 * word for a conversation PLUS THAT MARKET'S OWN "SERVICES" WORD (founder-ruled 2026-08-27: the
 * name should be formal, a full trading name and not a bare noun), AND THE LEGAL SUFFIX IS
 * PER-LOCALE TOO: each market sees its own national entity — Colloquy Services Ltd. ·
 * Viðræðuþjónustan ehf. · Coloquio Servicios S.L. · Dysputa Usługi sp. z o.o. · Ordskifte
 * Tjenester AS — which is peak corporate pomposity for an outfit whose flagship seller rents a
 * booth above a carpet showroom.
 *
 * THE JOKE IS STRUCTURAL AND NOTHING ON THE PAGE SAYS IT. The shelf section this row sits in is
 * CHATS — informal *by constitution* (chat-pack §1: whimsical, funny, the informal word for the
 * informal promise) — and the company inside it calls itself Colloquy Services Ltd. and prints a
 * group structure at the foot of its page. Neither half is aware of the other. That is the whole
 * gag, and it is made of two ordinary facts, which is why it survives the deadpan rule.
 *
 * ⚠ NOTHING ELSE ON THE PAGE IS TRANSLATED, and that is not an oversight to finish: the strap,
 * the listings, the review and the terms are all placeholder English, and translating placeholder
 * copy would fix drawing-board vocabulary into five languages instead of one. The name is the
 * exception because being per-market IS the content — an English page headed *Viðræður* is the
 * intended reading, not a half-done one.
 */

import type { Locale } from '../lib/i18n'

/**
 * A TRADING NAME AND THE LEGAL FORM THAT GOES WITH IT — ONE OBJECT, NEVER SPLIT.
 *
 * The three fields are one legal person, so they are one record and they fall back together. That
 * is a deliberate deviation from `ScenarioCopy`, whose fallback is per FIELD: there, an Icelandic
 * title beside an English blurb is honestly half-authored. Here the same mechanism would print
 * *Coloquio Servicios Ltd. · registered in London*, which is not half a translation but a wrong
 * company.
 */
export interface Entity {
  /**
   * The trading name. Alone in the header (a company's own masthead never carries its legal
   * form); WITH the suffix on the shelf row and the legal line, which both speak ABOUT the
   * company rather than as it (founder-ruled 2026-08-27: the shelf shows the full formal name).
   */
  brand: string
  /** The legal form. Printed after the brand on the shelf row and the legal line. */
  suffix: string
  /** Where it is registered. Printed on the legal line and nowhere else. */
  seat: string
}

/** One purchasable tier inside a listing. `price` is a string: it is printed, never computed. */
export interface Tier {
  name: string
  price: string
  body: string
}

/** The one review a listing shows, and what the seller did about it. */
export interface Review {
  /** 1–5. Rendered as filled and empty stars; never as a number the reader has to convert. */
  stars: number
  when: string
  body: string
  /** Reported as a plain count, in the site's voice. The site does not comment on it. */
  sellerResponse: string
}

/** One thing the company sells, or includes. */
export interface Listing {
  id: string
  title: string
  /** Who provides it — a seller's display name, or a vendor line for something the site licenses. */
  provider: string
  /**
   * The listing's standing, as the site prints it: a star rating for a seller, a satisfaction
   * figure for a product. A string because the two are not the same measurement and the page
   * should not pretend they are.
   */
  standing: string
  /** Short facts under the title — duration, medium, response time. Joined with · on the page. */
  meta: string[]
  body: string
  tiers?: Tier[]
  /** Small print belonging to this listing alone. */
  note?: string
  /**
   * What the control says. A diegetic verb — the reader is booking a slot on a marketplace, not
   * pressing PLAY on a scenario.
   */
  action: string
  /** ⚠ A PACK ADDRESS, and the pack's own URL keeps working without this page. */
  href: string
  review?: Review
}

/**
 * A ROSTER ROW — the BARE genre of company page (founder-ruled 2026-08-27, for Fóstbræður: *"it's
 * way too busy. it should literally just be the title, subtitle, and a list of characters… this
 * page doesn't need to be realistic like the other one"*). One person, one sentence, one address.
 *
 * So there are now TWO page genres and the data decides which renders: a company with `listings`
 * is the marketplace DOCUMENT (Colloquy — the craft rule above, terms, review, legal line, all of
 * it); a company with `people` is a ROSTER — title, strap, rows, and nothing else on the page.
 * The straight-faced fiction still exists for a roster company, but it lives in the shelf hook
 * and the packs, not in page furniture.
 *
 * ⚠ `line` is overlayable per locale because these are the founder's OWN sentences — the first
 * founder-authored content string on any company page arrived in Icelandic with the ruling.
 */
export interface Person {
  id: string
  name: string
  /** One sentence under the name. The base (shown for `en` and any unauthored locale). */
  line: string
  /** Per-locale lines; same per-field fallback as everything else. */
  lines?: Partial<Record<Exclude<Locale, 'en'>, string>>
  /** ⚠ A PACK ADDRESS, same law as `Listing.href`: the pack's own URL keeps working without this page. */
  href: string
}

export interface Company {
  /**
   * ADDRESSES THIS COMPANY USED TO HAVE, and still answers to.
   *
   * ⚠ THE SOCIAL-OBJECT LAW, MADE CHEAP. An address a reader was sent must open — which is why
   * `handled` sat in the URL long after the name went, and why the note below argued against
   * renaming. With an alias list that argument dissolves: the slug can follow the brand and the
   * old link still resolves, so a rename costs a line rather than a broken address.
   *
   * Never rendered. `hrefForCompany` always writes the current id, so a followed alias quietly
   * becomes the new address the next time anybody shares it.
   */
  aliases?: string[]

  /** The URL segment, and the manifest key. Never shown. */
  id: string
  /**
   * ⚠ THE NAME IN THE BASE LANGUAGE, DECLARED ONCE. A rename in English is this line.
   *
   * English is the base and the fallback, exactly as `Scenario.title`/`blurb` are the base under
   * `Scenario.copy`: a locale that has not been authored shows this rather than nothing.
   */
  entity: Entity
  /**
   * ⚠ THE OTHER FOUR — FOUNDER'S PEN, and every one of them is BUILDER-PROPOSED.
   *
   * Resolved by `entityFor`, the same shape and the same fallback as `copyFor`/`copyForConversation`
   * in `scenarios.ts`: an overlay keyed by non-English locales over the English base, so adding a
   * language never touches an existing entry and an unauthored one is visibly absent rather than a
   * map quietly full of holes.
   */
  locales?: Partial<Record<Exclude<Locale, 'en'>, Entity>>
  /**
   * ⚠ WHICH LOCALE'S ENTITY OWNS THE OTHERS — the group line at the foot of the page.
   *
   * A locale rather than a name, so the parent is not a sixth place the company is spelled: the
   * footer looks its own entity up through `entityFor` like everybody else. The page prints the
   * line only for readers who are NOT already looking at the parent — a company is not a
   * subsidiary of itself, and a real group footer omits it in the parent's own market too.
   *
   * ⚠ DELETING THIS ONE LINE REMOVES THE LINE FROM ALL FIVE LOCALES. It is the peak-pomposity
   * detail rather than a fact the page needs, and it is the first thing to cut if the foot of the
   * page ever reads as clutter.
   */
  parentLocale?: Exclude<Locale, 'en'>
  /** One line under the name. States the offer; never the joke. */
  strap: string
  /** The marketplace genre. A company with listings renders the full document. */
  listings?: Listing[]
  /** The roster genre. A company with people renders title · strap · rows, and nothing else. */
  people?: Person[]
  /** The foot of the page. Ordinary marketplace terms; absent on a roster page. */
  terms?: string[]
  /** ⚠ FOUNDER'S PEN — the CHATS row's own two lines. Deadpan; it never winks and never explains. */
  shelfHook: string
  /**
   * WHICH SHELF THIS SITS ON — absent means CHATS (2026-08-27).
   *
   * ⚠ THE SECTION IS A PROMISE ABOUT TONE, never about rendering surface. CHATS is humorous by
   * definition; DISCOURSES is the sincere lane. A company is the same *form* on either — a card,
   * a page, people behind it — and the heading above it is what says which register the reader is
   * being offered. So this is one field rather than two types.
   */
  shelf?: 'chats' | 'discourses' | 'learning'
  /** Off the shelf, but kept. Same convention as `Scenario.active`: absent is off. */
  active?: boolean
  /**
   * ON THE SHELF, BUT NOT YET A DOOR — absent means enterable.
   *
   * The row's version of `Scenario.enterable`, and it means exactly the same thing: `active`
   * decides whether a reader SEES this, and this decides whether they can go in. A thing that is
   * coming is a different fact from a thing that is withheld, and collapsing them loses the
   * announcement — which is the whole point of leaving it on the page.
   *
   * ⚠ A SHELF FACT, NOT A GATE. The address keeps working, on the rule that a link somebody was
   * sent must open. Closing a pack for real is the server's job.
   */
  enterable?: boolean

}

export const COMPANIES: Company[] = [
  /*
   * ⚠ ORDER IN THIS LIST IS ORDER ON THE SHELF, and the stagger runs through it — the leftmost
   * card is also the first to arrive. Same convention as `scenarios.ts`.
   *
   * FÓSTBRÆÐUR LEADS (founder-ruled 2026-08-27), and the reason outlives this particular
   * arrangement: **it is the only door anybody can currently open on its shelf.** Colloquy is
   * held, so leading with it would land a reader's eye on a greyed card and make the shelf's
   * first impression a thing they cannot have. The working door goes first.
   *
   * ⚠ THE ACADEMY WAS THE OTHER CARD NAMED IN THIS NOTE AND IS NEITHER HELD NOR HERE ANY MORE: it
   * moved to LEARNING on 2026-08-28 and opened on 2026-08-31. The rule did not change; the roll
   * call did.
   */
  {
    id: 'fostbraedur',
    entity: { brand: 'Fóstbræður', suffix: 'sf.', seat: 'Reykjavík' },
    strap: 'Characters from the classic icelandic sketch show Fóstbræður.',
    shelfHook:
      'Characters from the classic icelandic sketch show Fóstbræður.',
    active: true,
    people: [
      {
        id: 'indridi',
        name: 'Indriði',
        line: 'Indriði is looking for somebody to attend to his complaints',
        lines: {
          is: 'Indriði er í leit að einhverjum til að sinna kvörtunum sínum',
          /* ⚠ The three below are builder strawmen of the two approved lines above
             (2026-08-27, the all-languages commission); es/pl lack a native gate on staff. */
          es: 'Indriði busca a alguien que atienda sus quejas',
          pl: 'Indriði szuka kogoś, kto zajmie się jego skargami',
          no: 'Indriði er på utkikk etter noen som kan ta seg av klagene hans',
        },
        href: '/p/indridi',
      },
      /*
       * ⚠ THE SECOND CARD, AND THE PAGE'S REASON FOR EXISTING ARRIVES WITH IT (2026-09-18). It
       * was built for one man, which made it a page about a person; two make it what it was named
       * for — a cast. The order is the order they were made.
       *
       * ⚠ THE ICELANDIC LINE IS A BUILDER'S AND WANTS THE FOUNDER'S EAR, unlike Indriði's above,
       * which he approved. The three below it are strawmen off the Icelandic on the usual footing.
       *
       * ⚠ AND THIS ROW IS THE ONE PLACE THE CARD IS NAMED IN FIVE LANGUAGES WHILE THE CHARACTER
       * SPEAKS ONLY ONE. The pack is Icelandic-native and has no sidecars (see its description):
       * the seed's demonstrations are the sketch's own lines and do not survive translation. So a
       * reader in Spanish reads this row in Spanish and then meets a man writing Icelandic at
       * them. That is the open question on the card, not an oversight — it may be right, since the
       * joke is only a joke to people who know the sketch, but it is the founder's call and it is
       * unmade.
       */
      {
        id: 'spliff-donk-gengja',
        name: 'Sölumaðurinn',
        line: 'The salesman is working down his list, and your number is next',
        lines: {
          is: 'Sölumaðurinn er að vinna sig niður listann, og þitt númer er næst',
          es: 'El vendedor va bajando por su lista, y tu número es el siguiente',
          pl: 'Sprzedawca schodzi w dół swojej listy, a twój numer jest następny',
          no: 'Selgeren jobber seg nedover lista, og nummeret ditt er det neste',
        },
        href: '/p/spliff-donk-gengja',
      },
    ],
  },
  {
    /*
     * ⚠ RENAMED `handled` → `colloquy` (founder, 2026-08-27), and the old address still opens.
     *
     * The note that stood here argued the opposite — that `/c/handled` was an address somebody may
     * already hold, and that "renaming it would buy a tidier string and cost a working link." The
     * link was the real objection, and `aliases` answers it: `/c/handled` resolves to this company
     * forever, while every link the shelf writes from now on says `colloquy`.
     *
     * Which leaves only the tidier string — and that turned out to be worth having, because the
     * company's whole joke is its self-seriousness, and a slug reading `handled` is the site
     * telling on it from outside the fiction.
     */
    id: 'colloquy',
    aliases: ['handled'],
    /*
     * ⚠ HELD ON THE SHELF (founder-ruled 2026-08-27) — Viðræðuþjónustan / Colloquy, visible and
     * greyed, on the same rule as Hildur and Silfurberg. (Ivor stood in that list until
     * 2026-08-31, when the Academy card opened and stopped holding him.)
     *
     * ⚠ AND IT HOLDS EVERYONE BEHIND IT, which is worth stating because it is not obvious. An
     * earlier draft of this note claimed Otto kept his own shelf row — he does not: the three
     * conversation rows (`the-argument`, `handled-support`, `indridi`) are all `active: false`
     * since the company pages became the way in. So this page IS the only shelf route to Victor and
     * to Otto, and holding it holds them.
     *
     * Fóstbræður is unaffected and still opens Indriði.
     *
     * Deleting this one line is the entire re-enable.
     */
    enterable: false,
    /*
     * ⚠ ALL FIVE NAMES ARE BUILDER-PROPOSED, PENDING THE FOUNDER'S PEN. Each is a dignified,
     * slightly pompous word for a conversation in its own language, made formal with that
     * market's own "services" word (founder-ruled 2026-08-27) and paired with its ordinary
     * legal form. Formality patterns per market: English *X Services Ltd.* · Spanish brand-first
     * *X Servicios S.L.* (the Ferrovial Servicios pattern) · Polish *X Usługi sp. z o.o.* ·
     * Norwegian *X Tjenester AS* · Icelandic AS A COMPOUND, because Icelandic does not
     * juxtapose — *Viðræðuþjónustan*, definite, the national-institution ring of Póstþjónustan.
     *
     * ⚠ THE ICELANDIC ESPECIALLY — the founder is the native gate and offered two alternates when
     * the naming was first ruled: **Orðræða** (discourse, the public kind) and **Rökræða**
     * (reasoned debate). Either substitutes inside the compound (Orðræðuþjónustan ·
     * Rökræðuþjónustan); `viðræðu-` is the most institutional of the three, which is why it is
     * here and why it is also the parent. The definite article is a second gate: the indefinite
     * **Viðræðuþjónusta ehf.** is the one-letter alternative.
     *
     * The seats are the ordinary registered-office cities and carry no joke of their own.
     */
    entity: { brand: 'Colloquy Services', suffix: 'Ltd.', seat: 'London' },
    locales: {
      is: { brand: 'Viðræðuþjónustan', suffix: 'ehf.', seat: 'Reykjavík' },
      es: { brand: 'Coloquio Servicios', suffix: 'S.L.', seat: 'Madrid' },
      pl: { brand: 'Dysputa Usługi', suffix: 'sp. z o.o.', seat: 'Warszawa' },
      no: { brand: 'Ordskifte Tjenester', suffix: 'AS', seat: 'Oslo' },
    },
    /*
     * The Icelandic entity is the parent, which is the joke at its most complete: four national
     * subsidiaries of a two-letter-suffix company in Reykjavík, selling five minutes of argument
     * from a booth above a carpet showroom. It also makes the structure legible to a reader who
     * only ever sees ONE locale — without it, each market just sees an ordinary company.
     */
    parentLocale: 'is',
    strap: 'Freelance services, booked by the slot. Fixed rates, shown before you book.',
    shelfHook:
      'A freelance-services marketplace. Book five minutes of professional disagreement from booth four, or open support, which is free.',
    active: true,
    listings: [
      {
        id: 'booth-four',
        title: 'Booth four — professional disagreement',
        provider: 'VICTOR A.',
        standing: '★ 4.9 · 406 sessions',
        meta: ['five minutes', 'conducted in writing', 'usually opens within a minute'],
        body:
          'I take the opposite of your position and hold it for the booked time. Bring a position. Any position will do.',
        tiers: [
          {
            name: 'BASIC',
            price: '£14',
            body: 'Opposition. I take the other side of whatever position you put to me, and hold it.',
          },
          {
            name: 'PREMIUM',
            price: '£24',
            body: 'Counterexamples. Worked cases and hypotheticals.',
          },
          {
            name: 'ENTERPRISE',
            price: '£40',
            body: 'Sources. Anything with a name and a date attached to it.',
          },
        ],
        note: 'Upgrades are taken at booking. The site cannot process an upgrade once a session has opened.',
        action: 'Book five minutes',
        href: '/p/the-argument',
        review: {
          stars: 1,
          when: '2 years ago',
          body:
            'this wasn’t a real argument, he just disagreed with everything i said for five minutes. avoid.',
          sellerResponse: 'The seller has responded to this review 61 times.',
        },
      },
      {
        id: 'support',
        title: 'Otto 4.2 — support assistant',
        provider: 'Supplied under licence by Ilex Systems · updated March 2026',
        standing: '94% of customers rate it helpful',
        meta: ['included at no cost', 'answers in seconds', 'open at every hour'],
        body:
          'Bookings, tiers, payments, refunds, seller guidance and complaints. Support is included with every account. The booths are not.',
        action: 'Open support',
        href: '/p/handled-support',
      },
    ],
    terms: [
      'Sessions are conducted in writing. The seller’s clock starts when the session opens.',
      'Rates are per session and are shown before booking. Upgrades are taken at booking only.',
      'Refunds are at the seller’s discretion.',
      'Complaints are logged and reviewed. Every complaint receives a reply.',
      'The site takes 18% of each booking. Sellers set their own rates and hold their own ratings.',
    ],
  },

  /*
   * FÓSTBRÆÐUR — the second company, founder-commissioned 2026-08-27 ("functions exactly like
   * the Colloquy services site, and put Indriði in there").
   *
   * ⚠ EVERY CONTENT STRING IS BUILDER-AUTHORED PENDING THE FOUNDER'S PEN. The NAME is the
   * founder's own, and it is doing the heavy lifting: for the Icelandic market it carries
   * name-recognition the way the founder's playtest showed Indriði himself does; for everyone
   * else the page must be funny the way the Colloquy page is funny — a straight-faced listing
   * of an absurd service. Nothing below explains the name, in either direction.
   *
   * THE BUSINESS: a PLACEMENT SERVICE FOR UNRESOLVED MATTERS. People register matters; the firm
   * finds each matter a recipient; the recipient is written to until the matter is resolved.
   *
   * ⚠ SIMPLIFIED TO THE ROSTER GENRE, founder-ruled 2026-08-27: *"it's way too busy. it should
   * literally just be the title, subtitle, and a list of characters. (this page doesn't need to
   * be realistic like the other one)."* The matter listing, the two-star review, and the terms
   * were removed with the ruling (git history holds them); the placement fiction survives in the
   * shelf hook and the strap, and the page itself is now `people` rows. Indriði's `is` line is
   * THE FOUNDER'S OWN SENTENCE, given verbatim with the ruling — the first founder-authored
   * content string on any company page; the English base ("…attend to his complaints") was a
   * builder's rendering and is FOUNDER-APPROVED 2026-08-27, in those words.
   *
   * ⚠ THE CANON IS ONE-DIRECTIONAL, BY LAW. The pack's seed (v0.4.0, 2026-09-02) says Indriði
   * has a number "on a slip of paper from the kitchen drawer" and does not remember what it was
   * for, and the pack NEVER names this firm — the page explains the number from outside, the way
   * `/c/handled` explains Victor's booth, and inside the conversation the arrangement stays
   * exactly as unexplained as the seed leaves it. Nothing on this page fixes
   * WHICH matter he opens with either; the pack decides that in play.
   *
   * ⚠ ONE ENTITY, NO `locales` OVERLAY, AND THAT IS THE READING, NOT AN OMISSION: Colloquy is a
   * multinational with pompous national subsidiaries; Fóstbræður is a two-man Reykjavík
   * partnership that trades under its own name wherever its letters happen to land, the way
   * Eimskip does. Translating the name would delete it. The overlay is one edit away if the
   * founder rules otherwise.
   *
   * ⚠ THE SUFFIX IS THE NATIVE GATE'S: `sf.` (sameignarfélag) is the old small-partnership form
   * and the right register for two sworn brothers with a filing cabinet; `ehf.` is the modern
   * alternative and a one-token swap.
   *
   * Built to take more rows: the roster is where future Icelandic-flavoured characters land
   * without a new surface — one person, one sentence, one address each.
   */
  /*
   * THE LANGUAGE SCHOOL — the fourth company, and the LEARNING shelf's working door
   * (founder-ruled 2026-08-28, correcting the builder's shortcut: *"the Learning shelf has a
   * specific language-learning page, similar to the company pages. then you pick a language
   * first. 'Marta' just happens to be the teacher."* The avenue's own §4.2: the roster rows are
   * LANGUAGES, each opening its teacher.)
   *
   * ⚠ FIRST IN THE LEARNING GRID, AND SINCE 2026-08-31 NO LONGER BY THE WORKING-DOOR RULE (the
   * Fóstbræður precedent). The Academy has shared this shelf since 2026-08-28 and was held when
   * this order was set, so the door a reader could actually open led — and the line above calling
   * this the shelf's working door was true of that day. The Academy opened on 2026-08-31: both
   * cards are doors now, and the order is a plain first-arrival one. Kept exactly as it stands,
   * because the ordering is the founder's to rule on and not a builder's to quietly redo the day
   * its original reason expires.
   *
   * ⚠ THE SAME FORM AT A THIRD TEMPERATURE, per the Academy's note: Colloquy is funny by
   * disproportion; the Academy earns its formality by keeping real methods; the school earns its
   * plainness by BEING WHAT IT SAYS — the Learning shelf's constitution is sincere AND overt, so
   * nothing here is a joke and nothing is a wink. It is a school. The claims cap governs the
   * copy: a teacher, tuition — never certification, never outcomes.
   *
   * ⚠ THE ROSTER ROWS ARE LANGUAGES, NOT PEOPLE — the one place this page reads the `Person`
   * type against its name. Deliberate: you pick the language first, and the teacher is who the
   * language turns out to come with. The teacher is named in the row's LINE, incidentally, the
   * way a small school's prospectus does it. Future languages land as rows (avenue §7b: the
   * dialect frontier — Norwegian-as-actually-spoken is the standing candidate).
   *
   * ⚠ NAME, STRAP, HOOK AND THE ROW LINE ARE BUILDER PLACEHOLDERS, THE PEN'S. The entity name is
   * doubly provisional: the spin-off naming is PARKED (avenue §8b, the burnt-namespace map), and
   * when that name lands, this school may simply take it — the shelf entity is where the brand
   * would first appear. One entity, no locales overlay, same reading as Fóstbræður: a school
   * that trades under its own name wherever its students happen to live.
   */
  {
    id: 'languages',
    shelf: 'learning',
    entity: { brand: 'The School of Languages', suffix: '', seat: 'Reykjavík' },
    /*
     * ⚠ THE ICELANDIC NAME (founder-commissioned 2026-09-02, after the tabbed shelf made the
     * gap visible: the school was rendering in English directly beside *Stofnunin stofnuð 1904*
     * on an Icelandic shelf, which reads as an oversight rather than as a company that trades
     * internationally).
     *
     * DEFINITE FORM, in the house style every Icelandic entity here uses — Stofnunin,
     * Viðræðuþjónustan, Akademían. *Tungumálaskólinn* is the plain compound and says exactly
     * what the English says; *Málaskólinn* is the shorter alternative and the pen's call.
     *
     * NO SUFFIX, because the English has none: this company advertises no founding date and no
     * legal form, and inventing one for a single market would make it a different company in
     * Iceland than it is anywhere else.
     *
     * ⚠ BUILDER STRAWMAN PENDING THE FOUNDER'S GATE, like every other Icelandic string in this
     * file. And ⚠ es/pl/no are still absent — they fall back whole to the English entity, so
     * those three markets read the school in English while the Institute beside it trades under
     * four names.
     */
    locales: {
      is: { brand: 'Tungumálaskólinn', suffix: '', seat: 'Reykjavík' },
      /*
       * ⚠ THE SEAT DOES NOT MOVE, and this is where the school parts company with the Institute
       * one card down. That one takes a different seat in every market — London · Reykjavík ·
       * Madrid · Warszawa · Oslo — because it trades as a national entity in each. This one's
       * ENGLISH entity is already seated in Reykjavík rather than in an English-speaking city,
       * which says it is a Reykjavík company that teaches abroad, not a company with branches.
       * Moving it to Oslo for the Norwegian overlay would invent a Norwegian office nobody has
       * authored. ⚠ Founder's call if the school is meant to localise like the Institute does.
       */
      no: { brand: 'Språkskolen', suffix: '', seat: 'Reykjavík' },
      /*
       * ⚠ NEITHER OF THESE TWO HAS A GATE — Spanish has no native reader on staff and Polish
       * belongs to the tester, so both ship as strawmen exactly like the school's other strings.
       *
       * `La Escuela de Idiomas` takes the article, in the house style `El Instituto` set on the
       * card below: Spanish names an institution with one, and dropping it would read as a
       * signboard rather than a company. `Idiomas` over `Lenguas` because a school teaches
       * idiomas — lenguas is the linguist's word and this page never lectures.
       *
       * `Szkoła Językowa` is the adjectival form, which is what Polish actually calls this kind
       * of school. `Szkoła Języków Obcych` is the fuller standard term and was left: *obcych*
       * means FOREIGN, a word the English name does not say and which quietly decides whose
       * point of view the school is named from.
       */
      es: { brand: 'La Escuela de Idiomas', suffix: '', seat: 'Reykjavík' },
      pl: { brand: 'Szkoła Językowa', suffix: '', seat: 'Reykjavík' },
    },
    strap: 'One teacher to a language; one student at a time. Choose your language.',
    shelfHook:
      'Languages, taught by a person who will get to know you. Choose yours; the teacher takes one student at a time.',
    active: true,
    people: [
      {
        id: 'castellano',
        name: 'Castellano',
        /*
         * ⚠ BUILDER-AUTHORED, the pen's. The row is the LANGUAGE; the teacher arrives inside the
         * sentence. Rioplatense is named by its city rather than by a linguist's word — the
         * page never lectures.
         */
        line: 'As Buenos Aires speaks it. The teacher is Marta — thirty years at the blackboard, one student at a time.',
        lines: {
          /* ⚠ Builder strawman; the founder is the native gate. es/pl/no owed with the rest. */
          is: 'Eins og það er talað í Buenos Aires. Kennarinn er Marta — þrjátíu ár við töfluna, einn nemandi í einu.',
        },
        href: '/p/castellano',
      },
      {
        id: 'islenska',
        name: 'Íslenska',
        /*
         * ⚠ BUILDER-AUTHORED, the pen's. The second language row (2026-08-29): Icelandic for
         * Polish speakers — the mirror pair, and the roster test (packs/islenska/sheet.md is
         * the source). The teacher arrives inside the sentence, per the school's manner.
         */
        line: 'As Reykjavík speaks it. The teacher is Ewa — she crossed from Polish herself, twenty years ago. One student at a time.',
        lines: {
          /* ⚠ Builder strawmen; the Polish tester gates pl, the founder gates is. */
          pl: 'Islandzki, jakim mówi Reykjavík. Nauczycielką jest Ewa, która sama przeszła tę drogę z polskiego, dwadzieścia lat temu. Jeden uczeń na raz.',
          is: 'Íslenska eins og hún er töluð í Reykjavík. Kennarinn er Ewa, sem fór sjálf þessa leið úr pólsku fyrir tuttugu árum. Einn nemandi í einu.',
        },
        href: '/p/islenska',
      },
    ],
  },

  {
    /*
     * THE ACADEMY — the third company, and the first on the DISCOURSES shelf (founder-ruled
     * 2026-08-27: *"The Academy, per locale entities. move Ivor inside it."*)
     *
     * ⚠ THE SAME FORM AT A DIFFERENT TEMPERATURE, and that is the whole difficulty of it. Colloquy
     * is funny BECAUSE it is a marketplace selling professional disagreement, and Fóstbræður
     * because it places unresolved matters — in both, the institution's self-seriousness is
     * disproportionate to what it does, and the disproportion is the joke. Repeat that here and
     * the sincere lane inherits comedy it does not want.
     *
     * So the difference is not the voice, it is whether the purpose is genuinely worth doing. An
     * Academy earns its formality by keeping several real methods of thinking-with, which is also
     * what makes choosing between them mean something: *I want to be listened to* is a different
     * need from *I want to be taken apart*.
     *
     * ⚠ AND THAT IS WHAT THE ROSTER IS: methods, not celebrities. Ivor listens and says what he
     * noticed, once. A questioner would be the interrogative door — which is exactly why Socrates
     * would belong here and did NOT belong as a rename of Ivor: inside a group of methods his
     * elenchus is the catalogue rather than a contradiction.
     *
     * ⚠ THE QUESTIONER ARRIVED 2026-08-31, and the design answered the famous-names worry before
     * it could bite: Sokratis is a LIVING GREEK WHO HOLDS A CHAIR NAMED FOR THE PHILOSOPHER, not
     * the philosopher (`packs/sokratis/sheet.md`; the modern frame and the chair are both
     * founder-ratified). So the roster gained a method and no celebrity, and the need-pair the
     * note above describes is now actually on the page: *I want to be listened to* is Ivor,
     * *I want to be taken apart* is Sokratis. ⚠ The warning stands for whoever comes third — if
     * this fills up with famous names, Discourses quietly becomes the historical-figures roster
     * from the wedge spec, which is a real product and a different one.
     *
     * ⚡ RENAMED 2026-08-31: THE ACADEMY TRUST → THE INSTITUTE, est. 1904 (founder-ruled, and
     * ⚠ EXPLICITLY TEMPORARY — his words: *"a good enough temporary name"*. It is a placeholder,
     * not a settled name. Do not treat it as ruled.)
     *
     * WHY THE OLD NAME HAD TO GO — two false friends pointing the same way, both at SCHOOL, on a
     * shelf that already has a real school on it:
     *   · **Academy** promises courses, students, a curriculum. These two teach nothing. Ivor
     *     listens while you lay a half-made thing out; Sokratis questions you through a deadlock.
     *     The founder's catch: *"'academy' first and foremost may not be what Sokratis and Ivor
     *     are doing… the language school is more of an 'academy'."* Two cards on the Learning
     *     shelf were competing for one meaning, and the wrong one was winning.
     *   · **Trust**, in England, is the standard term for the body that runs state schools (a
     *     multi-academy trust). It did not puzzle a reader, it misled one confidently. Note the
     *     overlays had always said *foundation* — Fundación · Fundacja · stiftelse · ses. — so
     *     English was the outlier carrying a meaning nobody intended.
     *
     * WHY *INSTITUTE* — it is dignified without naming a discipline or a price, it does not
     * teach, and it is the one form that renders cleanly in all four overlays with no legal-form
     * word-order fight (the weakness recorded here for a year: real usage puts the form FIRST in
     * es and pl — *Fundación La Academia* — while `Entity` renders `brand suffix`).
     *
     * WHY *est. 1904* IN THE SUFFIX — the alternative flagged here long ago, now taken. It is the
     * register the founder asked for ("formal and serious and established"), an old institution's
     * name genuinely is just its name, and a founding date is the least sinister thing an
     * institution can advertise — which matters, because a bare *The Institute* is the stock name
     * of every shadowy organisation in fiction. That trope runs on opacity; this card kills it by
     * saying plainly, on the same screen, that members receive by the hour and that one of them
     * mends chairs. ⚠ It renders as `THE INSTITUTE EST. 1904` on the shelf card and nowhere else
     * (the legal line is gated on `listings`, which a roster page does not have).
     *
     * ⚠ WHAT THE NAME IS FOR, and the reason Ivor belongs in it — founder-ruled the same sitting:
     * *"Ivor is a genius, regardless of what he does. could be e.g. the janitor."* The eminence is
     * real and it is **genius regardless of station**: the Institute's claim is that a first-rate
     * mind may be found anywhere, and a chair-mender beside a chair-holder is its boast, not an
     * awkwardness. That principle licenses whoever comes third without further justification, and
     * it keeps the card honest — you buy an hour with a good mind, never access to a famous one.
     * ⚠ NOT YET IN THE COPY: the `shelfHook` below does not say it. Founder's pen.
     *
     * ⚠ `id` STAYS `academy` — an internal key, not copy. Changing it would break `/c/academy`
     * and the `houses` lookup for a name that is explicitly temporary. Rename the id only when the
     * name is settled, and then in the same commit as the route.
     *
     * ⚠ ALL FIVE NAMES ARE BUILDER-RENDERED FROM THE FOUNDER'S ENGLISH, PENDING HIS PEN and the
     * testers' gates. The Icelandic takes the definite form — *Stofnunin* — in the house style of
     * Viðræðuþjónustan and Akademían before it; whether a compound (*Fræðastofnunin*) is the more
     * Icelandic thing is his call, and it is the overlay to read first.
     */
    /*
     * ⚠ MOVED TO THE LEARNING SHELF (founder-ruled 2026-08-28: "put 'The Academy Trust' under
     * the new Learning shelf. (the Discourses shelf is then not needed)"). The DISCOURSES
     * SECTION empties with this line and drops off the page by the empty-section rule — the
     * heading key, the `houses` lookup and Ivor's inactive row all stay, so a future sincere
     * lane that is not learning-shaped is one field away. The Discourses CONSTITUTION (the
     * sincere register, the no-promise-gap law) is untouched doctrine; what moved is a card.
     */
    id: 'academy',
    shelf: 'learning',
    entity: { brand: 'The Institute', suffix: 'est. 1904', seat: 'London' },
    locales: {
      is: { brand: 'Stofnunin', suffix: 'stofnuð 1904', seat: 'Reykjavík' },
      es: { brand: 'El Instituto', suffix: 'fundado en 1904', seat: 'Madrid' },
      pl: { brand: 'Instytut', suffix: 'zał. 1904', seat: 'Warszawa' },
      no: { brand: 'Instituttet', suffix: 'stiftet 1904', seat: 'Oslo' },
    },
    /*
     * ⚠ FOUNDER'S PEN. What the place is for, in the register of a plaque rather than an advert.
     * ⚠ REWRITTEN BY THE FOUNDER 2026-08-31, verbatim on his word: the old strap ended *"No fee,
     * no appointment kept for you"*, which the paid-frame re-cut turned into a live contradiction
     * — it stood directly above two rows that are now booked and paid (the player-reality law,
     * `docs/guides/the-sheet-and-the-scene.md` STEP 0). The hour is the unit in both packs, so the
     * plaque says so; *by arrangement* survives from his original, and still carries what it
     * always carried — nobody is on call, and the door is not a shopfront.
     */
    strap: 'Members receive by the hour, by arrangement.',
    /*
     * ⚠ FOUNDER'S PEN. It sells the OFFER, not the people — which is the whole reason Ivor moved
     * inside: a row headed IVOR asked a reader to already know who that was.
     */
    /*
     * ⚠ THE PRINCIPLE IS NOW IN THE COPY (founder-ruled 2026-08-31: *"add the principle to the
     * shelfHook"*). The middle sentence is **genius regardless of station** — his words, *"Ivor
     * is a genius, regardless of what he does. could be e.g. the janitor"* — and it is stated the
     * way an old institution states a principle: by what it has never required, never by boasting
     * about who it admits. That negation is doing three jobs. It tells a reader why a chair-mender
     * and a chair-holder are on one list without ever pointing at either. It licenses whoever
     * comes third — a fisherman, a nurse, a retired judge — with no further argument. And it
     * keeps the card honest against the covenant: what is sold is an hour with a good mind, never
     * access to a famous one, so the eminence never becomes a status product.
     */
    shelfHook:
      'People who will think a thing through with you, each in their own way. Membership has never depended on what a person does for a living. Bring what you are working on.',
    active: true,
    /*
     * ⚠ OPEN SINCE 2026-08-31 (founder-commissioned with Sokratis: *"activate him in the ui"*).
     *
     * `enterable: false` stood here from 2026-08-27, and its deletion was the entire re-enable
     * exactly as the note promised. The card is a door again — and it is the door for EVERYONE on
     * the roster below, since both rows are `active: false` in `scenarios.ts` and this card is
     * their only route from the shelf.
     *
     * ⚠ PUTTING THE LINE BACK IS THE ENTIRE RE-HOLD, and it would take the whole roster with it.
     * Their own addresses (`/p/ivor`, `/p/sokratis`) keep working either way: a shelf fact, never
     * a gate.
     *
     * Colloquy is still held, and so are Hildur and Silfurberg. The Academy is no longer one of them.
     */
    people: [
      {
        id: 'ivor',
        name: 'Ivor',
        /*
         * ⚠ REWRITTEN FROM HIS SHELF HOOK, deliberately. That hook ended *"he will not let a word
         * of it stand unopened"*, which promises examination — and his sheet is explicit that he is
         * NOT an examiner: he listens, says what he noticed once, and leaves it. The copy had
         * drifted toward the Socratic man the design rules out, which is most likely why Socrates
         * came to mind at all.
         *
         * What survives is the engine: he does not know your trade and says so, so you have to
         * build the thing for him — which is the anti-oracle differentiator, and the reason this
         * is not asking ChatGPT.
         */
        /*
         * ⚠ RE-LED 2026-08-31 (the roster ruling — see Sokratis' note below for the full reason).
         * His door — ASSEMBLY, the half-made thing — now leads the line, because a reader has to be
         * able to sort himself between these two rows before meeting either man. Everything that
         * was already here survives: the trade he does not know, and listening until it is all out.
         * ⚠ The `is` overlay was the founder's; the new clause in front of it is the builder's and
         * needs his pen again.
         */
        line: 'Bring the thing you have not said out loud yet. Ivor mends chairs, does not know your trade, and listens until the whole of it is there',
        lines: {
          /* ⚠ is was the founder's native gate before the re-lead; es/pl/no are the builder's, unreviewed. */
          is: 'Komdu með það sem þú hefur ekki sagt upphátt enn. Ívar gerir við stóla, þekkir ekki þitt fag, og hlustar þar til það er allt komið',
          es: 'Trae eso que aún no has dicho en voz alta. Ivor arregla sillas, no conoce tu oficio, y escucha hasta que esté todo fuera',
          pl: 'Przynieś to, czego jeszcze nie powiedziałeś na głos. Ivor naprawia krzesła, nie zna się na twoim fachu i słucha, aż wyjdzie całość',
          no: 'Ta med det du ikke har sagt høyt ennå. Ivor reparerer stoler, kan ikke faget ditt, og hører etter til alt er ute',
        },
        href: '/p/ivor',
      },
      {
        id: 'sokratis',
        name: 'Sokratis',
        /*
         * ⚠ EVERY STRING IN THIS ROW IS BUILDER-AUTHORED PENDING THE FOUNDER'S PEN — the English
         * base and all four overlays, same standing as Ivor's above (whose `is` is the founder's
         * and whose es/pl/no are the builder's, unreviewed). The DESIGN behind it is
         * founder-ratified 2026-08-31: the man, the chair, and what he does with you
         * (`packs/sokratis/sheet.md`).
         *
         * BUILT ON THE SAME THREE BEATS AS IVOR'S, because the roster reads as one voice: what he
         * does · the LIMIT he works under · what he will do for you. His limit is that he keeps no
         * position of his own, which is Ivor's *"does not know your trade"* at the other end of the
         * catalogue — the anti-oracle differentiator in both cases, and the reason neither is
         * asking a machine for the answer.
         *
         * ⚠ REWRITTEN 2026-08-31 — THE ROSTER RULING (founder, after testing him: *"this is
         * sounding like the same character but with two different intensity settings"*). The
         * belief-door is retired in the pack and had to go from the shelf with it, because the
         * differentiation has to be legible BEFORE anyone meets either man (the founder's own
         * condition). The two doors, and they are the reason there are two rows:
         *   · IVOR — ASSEMBLY. Too little shape: a half-made thing that wants laying out.
         *   · SOKRATIS — DIAGNOSIS. Stuck: a deadlock, which is two beliefs pulling.
         * Both lines now LEAD WITH THE ARRIVAL STATE (*bring the thing that…*) rather than with
         * the man, so a reader sorts by which one they are, not by whose name they recognise.
         *
         * ⚠ WHAT REPLACED "WHETHER": the honesty law still holds (a belief may be refined,
         * dropped, or confirmed — three real endings), and *"asks until you can say what you
         * actually think"* keeps it: it promises a finding, never a demolition. Any rewrite must
         * keep that property; *"until he shows you you're wrong"* would sell the wrong ending and
         * would also be the door nobody walks through — nobody books an hour to be corrected.
         *
         * ⚠ NO METHOD-WORD, ANYWHERE, IN ANY LANGUAGE. The line says what he does; it never labels
         * it, and the pack never labels it either (§4.6 of the sheet: he does not narrate his own
         * method and never says "Socratic"). ⚠ AND THERE IS NO OFFERING NAME AT ALL, by decision
         * (founder-ruled 2026-08-31): *The Examined Life* was retired rather than rewritten when
         * the belief-door was — it rendered nowhere, it is method-language that would smuggle the
         * retired frame back onto the shelf, and the recognition it carried is already carried by
         * THE SOCRATES CHAIR, which is diegetic. Two naming levels do the work: the house, then
         * the person. Do not mint a third. (Fuller note in `scenarios.ts`, his row.)
         *
         * ⚠ THE NAME IS NOT NATURALISED IN `is`, AND THAT IS THE POINT — the founder's pen should
         * look here first. Ivor becomes *Ívar* in Icelandic; Sokratis must NOT become *Sókrates*,
         * because the whole frame is that he is a living Greek and not the philosopher. So the MAN
         * stays Sokratis in every language while the CHAIR takes each language's ordinary name for
         * the philosopher (Sókratesarstóllinn · Cátedra Sócrates · Katedra Sokratesa ·
         * Sokrates-stolen). The distinction is the design, not a translation slip.
         */
        line: 'Bring the thing you have gone back and forth on for months. Sokratis holds the Socrates Chair, keeps no position of his own, and asks until you can say what you actually think',
        lines: {
          is: 'Komdu með það sem þú hefur velt fyrir þér mánuðum saman. Sokratis situr Sókratesarstólinn, hefur enga afstöðu sjálfur, og spyr þar til þú getur sagt hvað þér finnst í raun',
          es: 'Trae eso que llevas meses dando vueltas. Sokratis ocupa la Cátedra Sócrates, no defiende ninguna postura propia, y pregunta hasta que puedas decir lo que realmente piensas',
          pl: 'Przynieś to, nad czym krążysz od miesięcy. Sokratis zajmuje Katedrę Sokratesa, sam nie ma żadnego stanowiska i pyta, aż będziesz w stanie powiedzieć, co naprawdę myślisz',
          no: 'Ta med det du har gått fram og tilbake på i månedsvis. Sokratis innehar Sokrates-stolen, har ikke noe standpunkt selv, og spør til du kan si hva du faktisk mener',
        },
        href: '/p/sokratis',
      },
    ],
  },

]

/** What the shelf shows. */
export const activeCompanies = () => COMPANIES.filter((c) => c.active)

/** What each shelf shows. Absent `shelf` means CHATS — the default, and every company before the Academy. */
export const companiesOn = (shelf: 'chats' | 'discourses' | 'learning') =>
  COMPANIES.filter((c) => c.active && (c.shelf ?? 'chats') === shelf)

/**
 * A company id → its page.
 *
 * Searched regardless of `active`, on the same rule as `findScenario`: being off the shelf has
 * never meant the address stops working.
 */
export const findCompany = (id: string | undefined) =>
    COMPANIES.find((c) => c.id === id)
    // ⚠ THE ID FIRST, ALWAYS. An alias must never shadow a live id — if a future company is ever
    // named after another's retired slug, the company that owns it now wins.
    ?? COMPANIES.find((c) => c.aliases?.includes(id ?? ''))

/** A company's own address. One route, one segment, nothing else in it. */
export const hrefForCompany = (c: Company) => `/c/${c.id}`

/**
 * THE COMPANY AS IT TRADES IN ONE LANGUAGE, falling back WHOLE to the English base.
 *
 * Same resolution as `copyFor`/`copyForConversation` — `en` never looks at the overlay, every
 * other locale takes its own entry if there is one — and the difference is that it falls back as
 * one object rather than field by field. See `Entity` for why.
 */
export function entityFor(c: Company, locale: Locale): Entity {
  return (locale === 'en' ? undefined : c.locales?.[locale]) ?? c.entity
}

/** A roster row's sentence in one language — same resolution rule as `entityFor`, per field. */
export function personLineFor(p: Person, locale: Locale): string {
  return (locale === 'en' ? undefined : p.lines?.[locale]) ?? p.line
}

/**
 * THE PARENT, FOR A READER WHO IS NOT ALREADY LOOKING AT IT.
 *
 * Absent when the company declares no parent, and absent in the parent's own market — a company
 * is not a subsidiary of itself, and the footer would otherwise name Viðræður ehf. twice in one
 * line for the one reader who would notice.
 */
export function parentFor(c: Company, locale: Locale): Entity | undefined {
  if (!c.parentLocale || c.parentLocale === locale) return undefined
  return entityFor(c, c.parentLocale)
}
